<!--
  @description Toggle row for a single challenge category. Shows the category icon, a localized
  label, and a Switch control. Emits `categoryToggled` with the new boolean ref when the switch
  changes. Use in pack-configuration screens where players choose which categories to include.

  @example
  <CategoryToggle
    category="physical"
    :avatarType="AvatarTypes.BLUE_RED"
    :isToggled="true"
    @categoryToggled="onCategoryToggled"
  />
-->
<template>
  <div class="category-toggle">
    <img :src="getCategoryImageFromStringAndAvatar(category, avatarType)" alt="" />
    {{ getCategoryTypeFromString(category, locale) }}
    <Switch
      :id="category"
      :modelValue="isCategoryToggled"
      @update:modelValue="toggleCategory"
      :disabled="disabled"
    />
  </div>
</template>

<script setup lang="ts">
import { AvatarTypes } from '@/types/Enums'
import Switch from '@/components/common/form/switch/Switch.vue'
import { ref, type PropType } from 'vue'
import { getCategoryTypeFromString, getCategoryImageFromStringAndAvatar } from '@/types/TypeMethods'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()

const props = defineProps({
  /** The category identifier string (e.g. `'physical'`, `'time'`). Used to look up the
   *  localized label and the avatar-specific icon via `getCategoryTypeFromString` and
   *  `getCategoryImageFromStringAndAvatar`. */
  category: {
    type: String,
    required: true
  },
  /** The avatar color set currently in use. Determines which variant of the category icon
   *  is displayed so it visually matches the active avatar. */
  avatarType: {
    type: String as PropType<AvatarTypes>,
    required: true
  },
  /** Initial toggle state. The component manages its own local copy — changes are not
   *  automatically reflected back unless the parent reacts to `categoryToggled`. */
  isToggled: {
    type: Boolean,
    required: true
  },
  /** When true, the Switch control is disabled and the toggle cannot be changed. */
  disabled: {
    type: Boolean,
    required: false,
    default: false
  }
})

const isCategoryToggled = ref(props.isToggled)

const toggleCategory = (isToggled: boolean) => {
  isCategoryToggled.value = isToggled
  emit('categoryToggled', isCategoryToggled)
}

const emit = defineEmits(['categoryToggled'])
</script>

<style scoped lang="scss">
.category-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0rem 2rem;

  @include breakpoint-md {
    margin: 0rem;
  }

  img {
    height: 125px;

    @include breakpoint-md {
      height: 100px;
    }
  }
}
</style>
